import ElasticSearchAPIConnector from '@elastic/search-ui-elasticsearch-connector';
import { applyConditionalFilters, createConditionalFilters } from '@scripts/frontend/elastic/shared-filters';

interface ElasticConfig {
  host: string;
  index: string;
  apiKey: string;
  interceptSearchRequest?: (params: any, next: Function) => any;
}

interface AutocompleteResponseState {
  autocompletedResults: any[];
  autocompletedResultsRequestId: string;
  autocompletedSuggestions: Record<string, never>;
  autocompletedSuggestionsRequestId: string;
}

interface SearchBody {
  size: number;
  _source: string[];
  query?: any;
}

/**
 * Custom Elasticsearch connector that uses a PHP proxy in local development
 * and direct Elasticsearch connection in other environments
 */
class ProxyAwareElasticConnector {
  private connector: ElasticSearchAPIConnector;
  private isLocalDev: boolean;
  private proxyUrl: string;
  private index: string;

  constructor(config: ElasticConfig) {
    this.isLocalDev = window.aqJobsOptions?.environment === 'local';
    this.proxyUrl = window.aqJobsOptions?.restUrl || '';
    this.index = config.index;

    if (this.isLocalDev && this.proxyUrl) {
      // Create a modified config that points to our proxy
      const proxyConfig = {
        ...config,
        host: this.proxyUrl.replace(/\/$/, ''), // Remove trailing slash
        index: config.index, // Keep the original index name for validation
        apiKey: '', // Not needed for proxy
        interceptSearchRequest: this.createProxyInterceptor(config.interceptSearchRequest),
      };
      this.connector = new ElasticSearchAPIConnector(proxyConfig);
    } else {
      // Use direct connection for non-local environments
      this.connector = new ElasticSearchAPIConnector(config);
    }
  }

  /**
   * Builds the full proxy URL for Elasticsearch requests
   */
  private buildProxyUrl(endpoint: string = '_search'): string {
    return `${this.proxyUrl}elasticsearch/${this.index}/${endpoint}`;
  }

  /**
   * Makes a fetch request to the PHP proxy with proper headers
   */
  private async fetchProxy(url: string, body: any): Promise<Response> {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': window.aqJobsOptions?.restNonce || '',
      },
      body: JSON.stringify(body),
    });
  }

  private createProxyInterceptor(originalInterceptor?: (params: any, next: Function) => any) {
    return async ({ requestBody, queryString }: any, next: Function) => {
      // Apply original interceptor if provided
      if (originalInterceptor) {
        const result = await originalInterceptor({ requestBody }, (modifiedBody: any) => modifiedBody);
        requestBody = result;
      }

      // In local development, modify the request to use our proxy endpoint
      if (this.isLocalDev && this.proxyUrl) {
        const url = this.buildProxyUrl('_search') + (queryString ? `?${queryString}` : '');
        const response = await this.fetchProxy(url, requestBody);

        if (!response.ok) {
          throw new Error(`Proxy request failed: ${response.status} ${response.statusText}`);
        }

        return response.json();
      }

      // For non-local environments, use the standard flow
      return next(requestBody);
    };
  }

  /**
   * Builds the Elasticsearch query for autocomplete with phrase prefix matching
   */
  private buildAutocompleteQuery(searchTerm: string, searchFields: any): any {
    const fields = Object.entries(searchFields).map(([field, config]: [string, any]) => {
      return config?.weight ? `${field}^${config.weight}` : field;
    });

    return {
      bool: {
        should: [
          // Exact phrase prefix match (highest priority)
          {
            match_phrase_prefix: {
              job_title: {
                query: searchTerm,
                boost: 3,
              },
            },
          },
          // Partial word matching across fields
          {
            multi_match: {
              query: searchTerm,
              fields: fields,
              type: 'phrase_prefix',
              boost: 2,
            },
          },
          // Fallback to any matching words
          {
            multi_match: {
              query: searchTerm,
              fields: fields,
              type: 'best_fields',
              operator: 'or',
            },
          },
        ],
        minimum_should_match: 1,
      },
    };
  }

  /**
   * Transforms Elasticsearch hits to Search UI autocomplete format
   */
  private transformAutocompleteResults(hits: any[]): any[] {
    return hits.map((hit: any) => ({
      job_title: {
        raw: hit._source?.job_title || '',
      },
      _meta: {
        id: hit._id,
        score: hit._score,
        rawHit: hit,
      },
    }));
  }

  /**
   * Creates an empty autocomplete response
   */
  private createEmptyAutocompleteResponse(): AutocompleteResponseState {
    return {
      autocompletedResults: [],
      autocompletedResultsRequestId: '',
      autocompletedSuggestions: {},
      autocompletedSuggestionsRequestId: '',
    };
  }

  // Proxy methods to the underlying connector
  onAutocomplete = async (requestState: any, queryConfig: any): Promise<AutocompleteResponseState> => {
    // In local dev, bypass the connector entirely and use our proxy directly
    if (this.isLocalDev && this.proxyUrl) {
      const searchBody: SearchBody = {
        size: 5,
        _source: queryConfig?.results?.result_fields ? Object.keys(queryConfig.results.result_fields) : [],
      };

      // Add search query if present
      if (requestState.searchTerm && queryConfig?.results?.search_fields) {
        searchBody.query = this.buildAutocompleteQuery(
          requestState.searchTerm,
          queryConfig.results.search_fields
        );
      }

      // Add filters if present
      if (queryConfig?.results?.filters && queryConfig.results.filters.length > 0) {
        if (!searchBody.query) {
          searchBody.query = { bool: {} };
        }
        searchBody.query.bool.filter = queryConfig.results.filters.map((filter: any) => ({
          terms: { [filter.field]: filter.values },
        }));
      }

      try {
        const response = await this.fetchProxy(this.buildProxyUrl('_search'), searchBody);

        if (!response.ok) {
          throw new Error(`Autocomplete proxy request failed: ${response.status}`);
        }

        const data = await response.json();
        const transformedResults = this.transformAutocompleteResults(data.hits?.hits || []);

        return {
          autocompletedResults: transformedResults,
          autocompletedResultsRequestId: Date.now().toString(),
          autocompletedSuggestions: {},
          autocompletedSuggestionsRequestId: '',
        };
      } catch (error) {
        return this.createEmptyAutocompleteResponse();
      }
    }
    
    // For non-local environments, use the standard connector
    const result = await this.connector.onAutocomplete(requestState, queryConfig);
    return result;
  };

  onSearch = (...args: any[]) => this.connector.onSearch(...args);
  onAutocompleteResultClick = (...args: any[]) => this.connector.onAutocompleteResultClick(...args);
  onClickThrough = (...args: any[]) => this.connector.onClickThrough(...args);
}

/**
 * Factory function to create a proxy-aware Elasticsearch connector
 */
export function createElasticConnector(config: ElasticConfig): ElasticSearchAPIConnector {
  const proxyConnector = new ProxyAwareElasticConnector(config);
  return proxyConnector as unknown as ElasticSearchAPIConnector;
}

/**
 * Gets Elasticsearch configuration from window object
 */
function getElasticConfig() {
  return {
    host: window.aqJobsOptions?.elasticHost || '',
    apiKey: window.aqJobsOptions?.elasticAPIKey || '',
    jobsIndex: window.aqJobsOptions?.elasticJobIndex || '',
  };
}

/**
 * Create the main job listing connector with conditional filtering
 */
export function createJobListingConnector(): ElasticSearchAPIConnector {
  const ELASTIC_CONFIG = getElasticConfig();

  return createElasticConnector({
    host: ELASTIC_CONFIG.host,
    index: ELASTIC_CONFIG.jobsIndex,
    apiKey: ELASTIC_CONFIG.apiKey,
    interceptSearchRequest: async ({ requestBody }, next) => {
      // Apply conditional region and brand filtering
      const conditionalFilters = createConditionalFilters();

      if (conditionalFilters.length > 0) {
        // Ensure requestBody has a query structure
        if (!requestBody.query) {
          requestBody.query = { bool: { must: [] } };
        }

        // Apply filters
        applyConditionalFilters(requestBody.query);
      }

      return next(requestBody);
    },
  });
}

/**
 * Create a related jobs connector for the given job ID
 */
export function createRelatedJobsConnector(jobId: string): ElasticSearchAPIConnector {
  const ELASTIC_CONFIG = getElasticConfig();

  return createElasticConnector({
    host: ELASTIC_CONFIG.host,
    index: ELASTIC_CONFIG.jobsIndex,
    apiKey: ELASTIC_CONFIG.apiKey,
    interceptSearchRequest: async ({ requestBody }, next) => {
      requestBody.query = {
        bool: {
          must: [{
            more_like_this: {
              fields: ['job_title', 'job_description'],
              like: [{
                _index: ELASTIC_CONFIG.jobsIndex,
                _id: jobId,
              }],
              min_term_freq: 1,
              max_doc_freq: 20,
              max_query_terms: 10,
            },
          }],
          must_not: [{ // Exclude the current job from results
            term: {
              _id: jobId,
            },
          }],
        },
      };

      // Apply conditional region and brand filtering
      applyConditionalFilters(requestBody.query);

      return next(requestBody);
    },
  });
}
